import { computed, ref, watch } from "vue";
import { rowRange, searchableText, type ValueRow } from "./valueRow";

const VIRTUALIZE_ABOVE = 100;

export type RowListCommand =
	| { kind: "toggle"; row: ValueRow }
	| { kind: "select-all"; rows: ValueRow[] }
	| { kind: "deselect-all"; rows: ValueRow[] };

export type RowListOptions = {
	rows: () => ValueRow[];
	isChecked: (row: ValueRow) => boolean;
	canToggle?: (row: ValueRow) => boolean;

	initialLimit?: () => number | null;
	virtualizeAbove?: number;
};

const matches = (row: ValueRow, needle: string): boolean =>
	searchableText(row).some((text) => text.toLowerCase().includes(needle));

export const useRowList = ({
	rows,
	isChecked,
	canToggle = () => true,
	initialLimit = () => null,
	virtualizeAbove = VIRTUALIZE_ABOVE,
}: RowListOptions) => {
	const search = ref("");
	const expanded = ref(false);

	const anchorKey = ref<string | null>(null);

	const query = computed(() => search.value?.trim().toLowerCase() ?? "");
	const isFiltered = computed(() => query.value.length > 0);

	const matching = computed(() =>
		isFiltered.value
			? rows().filter((row) => matches(row, query.value))
			: rows()
	);

	const limit = computed(() => initialLimit());

	const isBounded = computed(
		() =>
			limit.value !== null &&
			!expanded.value &&
			matching.value.length > limit.value
	);

	const visible = computed(() =>
		isBounded.value
			? matching.value.slice(0, limit.value as number)
			: matching.value
	);

	const shouldVirtualize = computed(
		() => visible.value.length > virtualizeAbove
	);

	const identity = computed(() =>
		rows()
			.map((row) => row.key)
			.join("|")
	);

	watch(identity, () => {
		expanded.value = false;
		search.value = "";
		anchorKey.value = null;
	});

	const resolveClick = (
		row: ValueRow,
		mouseEvent?: { shiftKey?: boolean }
	): RowListCommand => {
		if (mouseEvent?.shiftKey) {
			const range = rowRange(visible.value, anchorKey.value, row.key).filter(
				(candidate) => canToggle(candidate)
			);

			if (range.length > 1) {
				return isChecked(row)
					? { kind: "deselect-all", rows: range }
					: { kind: "select-all", rows: range };
			}
		}

		anchorKey.value = row.key;

		return { kind: "toggle", row };
	};

	const showAll = () => {
		expanded.value = true;
	};

	return {
		search,
		query,
		isFiltered,
		matching,
		visible,
		isBounded,
		shouldVirtualize,
		anchorKey,
		resolveClick,
		showAll,
	};
};
